import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { Json } from "@/integrations/supabase/types";

export interface Place {
  id: string;
  slug: string;
  name: string;
  municipality: string | null;
  landskap: string | null;
  county: string | null;
  tags: string[];
  description_short: string | null;
  address_text: string | null;
  /** @deprecated Use contact_website */
  website: string | null;
  /** @deprecated Use contact_instagram */
  instagram: string | null;
  /** @deprecated Use contact_phone */
  phone: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  contact_website: string | null;
  contact_instagram: string | null;
  verified_at: string | null;
  status: 'draft' | 'public';
  created_at: string;
  updated_at: string;
  lat: number | null;
  lng: number | null;
  created_by: string | null;
  place_core: Json;
}

export type PlaceInsert = Omit<Place, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'verified_at' | 'contact_email' | 'contact_phone' | 'contact_website' | 'contact_instagram'> & { verified_at?: string | null; contact_email?: string | null; contact_phone?: string | null; contact_website?: string | null; contact_instagram?: string | null };
export type PlaceUpdate = Partial<PlaceInsert>;
export type PlaceInsertPayload = PlaceInsert;

// Legacy type alias for backward compatibility
export type AdminPlace = Place;
export type AdminPlaceInsert = PlaceInsert;
export type AdminPlaceUpdate = PlaceUpdate;

export function useAdminPlaces() {
  return useQuery({
    queryKey: ["admin-places"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("places")
        .select("*")
        .order("name", { ascending: true });
      if (error) throw error;
      return data as unknown as Place[];
    },
  });
}

export function usePublishedPlaces(searchQuery?: string) {
  return useQuery({
    queryKey: ["published-places", searchQuery],
    queryFn: async () => {
      let query = supabase
        .from("places")
        .select("id, slug, name, municipality, landskap, lat, lng, address_text")
        .eq("status", "public")
        .order("name", { ascending: true });
      if (searchQuery && searchQuery.trim().length > 0) {
        query = query.or(`name.ilike.%${searchQuery}%,municipality.ilike.%${searchQuery}%`);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data as { id: string; slug: string; name: string; municipality: string | null; landskap: string | null; lat: number | null; lng: number | null; address_text: string | null }[];
    },
  });
}

export function useAdminPlace(placeId: string | undefined) {
  return useQuery({
    queryKey: ["admin-place", placeId],
    queryFn: async () => {
      if (!placeId) return null;
      const { data, error } = await supabase
        .from("places")
        .select("*")
        .eq("id", placeId)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as Place | null;
    },
    enabled: !!placeId,
  });
}

export function useCreateAdminPlace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (place: PlaceInsertPayload) => {
      const { data, error } = await supabase
        .from("places")
        .insert([place as any])
        .select()
        .single();
      if (error) throw error;
      return data as unknown as Place;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-places"] });
      queryClient.invalidateQueries({ queryKey: ["published-places"] });
      toast({ title: "Plats skapad", description: "Den nya platsen har skapats." });
    },
    onError: (error) => {
      console.error("Error creating place:", error);
      toast({ title: "Kunde inte skapa plats", description: error.message, variant: "destructive" });
    },
  });
}

export function useUpdateAdminPlace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: PlaceUpdate }) => {
      const { data, error } = await supabase
        .from("places")
        .update({ ...updates, updated_at: new Date().toISOString() } as any)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as unknown as Place;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin-places"] });
      queryClient.invalidateQueries({ queryKey: ["admin-place", data.id] });
      queryClient.invalidateQueries({ queryKey: ["published-places"] });
      // Invalidate smart steering queries so match counts update when intents change
      queryClient.invalidateQueries({ queryKey: ["match-rules-count"] });
      queryClient.invalidateQueries({ queryKey: ["tag-suggestions"] });
      toast({ title: "Plats uppdaterad", description: "Ändringarna har sparats." });
    },
    onError: (error) => {
      console.error("Error updating place:", error);
      toast({ title: "Kunde inte uppdatera plats", description: error.message, variant: "destructive" });
    },
  });
}

export function useDeleteAdminPlace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("places").delete().eq("id", id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-places"] });
      queryClient.invalidateQueries({ queryKey: ["published-places"] });
      toast({ title: "Plats raderad", description: "Platsen har tagits bort." });
    },
    onError: (error) => {
      console.error("Error deleting place:", error);
      toast({ title: "Kunde inte radera plats", description: error.message, variant: "destructive" });
    },
  });
}
